import {useMemo, useState} from "react"
import {Alert, Badge, Button, Card, Col, Form, Row} from "react-bootstrap"

import {layoutDisplayName} from "./display"
import type {
    Circuit,
    FreeOutletType,
    Layout,
    Outlet,
    PlanningData,
    Seat,
    Survey,
} from "./types"

type SectionFrame = {
    key: string
    name: string
    index: number
    left: string
    width: string
    top: string
    height: string
}

export default function RoomSeatPlanComponent({
    data,
    selectedLayout,
    layoutKey,
    editing,
    outletTool,
    noWritingSurfaceTool,
    seatStructureTool,
    onInitialize,
    onSelectLayout,
    onOutletToolChange,
    onNoWritingSurfaceToolChange,
    onSeatStructureToolChange,
    onCreateOutlet,
    onEditOutlet,
    onEditSeat,
    onToggleWritingSurface,
    onCreateSeat,
    onDeleteSeat,
}: {
    data: PlanningData
    selectedLayout?: Layout
    layoutKey: string
    editing: boolean
    outletTool: FreeOutletType | null
    noWritingSurfaceTool: boolean
    seatStructureTool: "ADD" | "DELETE" | null
    onInitialize: () => void
    onSelectLayout: (key: string) => void
    onOutletToolChange: (type: FreeOutletType | null) => void
    onNoWritingSurfaceToolChange: (active: boolean) => void
    onSeatStructureToolChange: (tool: "ADD" | "DELETE" | null) => void
    onCreateOutlet: (type: FreeOutletType, position: {x: number; y: number}) => void
    onEditOutlet: (outlet: Outlet, circuit?: Circuit) => void
    onEditSeat: (seat: Seat, outlet?: Outlet, circuit?: Circuit) => void
    onToggleWritingSurface: (seat: Seat) => void
    onCreateSeat: (position: {x: number; y: number}) => void
    onDeleteSeat: (seat: Seat) => void
}) {
    const [hoveredSeatKey, setHoveredSeatKey] = useState<string | null>(null)
    const examSeats = useMemo(() => new Set(
        selectedLayout?.assignments.map(item => item.physicalSeatKey) ?? []
    ), [selectedLayout])
    const examSeatNumbers = useMemo(() => new Map(
        selectedLayout?.assignments.map(item =>
            [item.physicalSeatKey, item.examSeatNumber]) ?? []
    ), [selectedLayout])
    const seatByKey = useMemo(() => new Map(
        data.seatPlan?.seats.map(seat => [seat.key, seat]) ?? []
    ), [data.seatPlan])
    const examRows = useMemo(() => new Set(
        selectedLayout?.assignments.flatMap(assignment => {
            const seat = seatByKey.get(assignment.physicalSeatKey)
            return seat ? [`${seat.section}:${seat.row}`] : []
        }) ?? []
    ), [seatByKey, selectedLayout])
    const outletsBySeat = useMemo(() => new Map(
        data.installation?.outlets.filter(outlet => outlet.seatKey)
            .map(outlet => [outlet.seatKey!, outlet]) ?? []
    ), [data.installation])
    const circuitsByKey = useMemo(() => new Map(
        data.installation?.circuits.map(circuit => [circuit.key, circuit]) ?? []
    ), [data.installation])
    const neighborCounts = useMemo(() => {
        const counts = new Map<string, number>()
        for (const edge of data.seatPlan?.adjacency ?? []) {
            counts.set(edge.from, (counts.get(edge.from) ?? 0) + 1)
        }
        return counts
    }, [data.seatPlan])
    const validations = useMemo(() => new Map(
        data.survey?.seatValidations.map(item => [item.seatKey, item]) ?? []
    ), [data.survey])
    const frames = useMemo<SectionFrame[]>(() =>
        data.seatPlan?.sections.map((section, index) => {
            const seats = data.seatPlan!.seats.filter(
                seat => seat.section === section.key
            )
            const xs = seats.map(seat => seat.position.x)
            const ys = seats.map(seat => seat.position.y)
            const minX = Math.max(0, Math.min(...xs) - .018)
            const maxX = Math.min(1, Math.max(...xs) + .018)
            const minY = Math.max(0, Math.min(...ys) - .065)
            const maxY = Math.min(1, Math.max(...ys) + .035)
            return {
                ...section,
                index,
                left: `${minX * 100}%`,
                width: `${(maxX - minX) * 100}%`,
                top: `${minY * 100}%`,
                height: `${(maxY - minY) * 100}%`,
            }
        }) ?? [], [data.seatPlan])
    const hoveredSeat = seatByKey.get(hoveredSeatKey ?? "")

    return <>
        {!data.seatPlan && editing && <Alert variant="info">
            Für diesen Hörsaal existiert noch kein Sitzplan.
            <Button size="sm" className="ms-3" onClick={onInitialize}>
                Hörsaal einpflegen
            </Button>
        </Alert>}
        {data.installation?.powerAvailability === "confirmed" &&
            data.installation.mappingStatus === "unmapped" &&
            <Alert variant="warning">
                <strong>Stromversorgung vorhanden, aber nicht kartiert.</strong>
                {" "}Steckdosenanzahl, Positionen und Sicherungen sind noch
                unbekannt. Die angezeigte Stromabdeckung ist daher keine Aussage
                über die tatsächliche Versorgung.
            </Alert>}
        <Row className="g-3 mb-3">
            <Col><Card body><strong>{data.seatPlan?.seats.length ?? 0}</strong>
                <div className="text-muted">Physische Sitze</div></Card></Col>
            <Col><Card body><strong>
                {data.installation?.mappingStatus === "unmapped"
                    ? "unbekannt" : data.installation?.outlets.length ?? 0}
            </strong><div className="text-muted">
                Kartierte Steckdosenplätze
            </div></Card></Col>
            <Col><Card body><strong>{selectedLayout?.capacity ?? 0}</strong>
                <div className="text-muted">Belegte Sitze</div></Card></Col>
            <Col><Card body><strong>
                {selectedLayout?.calculatedNativePower
                    ?.poweredIncludingAdjacent ?? 0}
            </strong><div className="text-muted">
                Ohne Zusatzverteilung
            </div></Card></Col>
        </Row>
        <Form.Label htmlFor="seat-plan-layout">
            Angezeigte Sitzplatzaufteilung
        </Form.Label>
        <Form.Select id="seat-plan-layout" className="mb-2" value={layoutKey}
            onChange={event => onSelectLayout(event.target.value)}>
            {data.layouts.map(layout => <option key={layout.key}
                value={layout.key}>{layoutDisplayName(layout)}</option>)}
        </Form.Select>
        <p className="small text-muted">
            Alle Symbole sind physische Hörsaalsitze. Blau zeigt die in der
            ausgewählten Abstandspolicy belegten Sitze.
        </p>
        {editing && data.seatPlan &&
            <div className="d-flex flex-wrap gap-2 mb-3">
                <span className="align-self-center small fw-semibold me-1">
                    Objekt setzen:
                </span>
                <Button size="sm"
                    variant={noWritingSurfaceTool
                        ? "warning" : "outline-warning"}
                    onClick={() => {
                        onOutletToolChange(null)
                        onSeatStructureToolChange(null)
                        onNoWritingSurfaceToolChange(!noWritingSurfaceTool)
                    }}>
                    <i className="bi bi-pencil-slash me-2"/>
                    Keine Schreibfläche
                </Button>
                <Button size="sm"
                    variant={seatStructureTool === "ADD"
                        ? "success" : "outline-success"}
                    onClick={() => {
                        onOutletToolChange(null)
                        onNoWritingSurfaceToolChange(false)
                        onSeatStructureToolChange(
                            seatStructureTool === "ADD" ? null : "ADD"
                        )
                    }}>
                    <i className="bi bi-plus-circle me-2"/>
                    Sitz hinzufügen
                </Button>
                <Button size="sm"
                    variant={seatStructureTool === "DELETE"
                        ? "danger" : "outline-danger"}
                    onClick={() => {
                        onOutletToolChange(null)
                        onNoWritingSurfaceToolChange(false)
                        onSeatStructureToolChange(
                            seatStructureTool === "DELETE" ? null : "DELETE"
                        )
                    }}>
                    <i className="bi bi-trash me-2"/>
                    Sitz entfernen
                </Button>
                {([
                    ["WALL", "Wandsteckdose", "bi-plug"],
                    ["FLOOR", "Bodendose", "bi-square"],
                    ["LECTERN", "Pultanschluss", "bi-easel"],
                ] as const).map(([type, label, icon]) =>
                    <Button key={type} size="sm"
                        disabled={!data.installation}
                        variant={outletTool === type
                            ? "danger" : "outline-secondary"}
                        onClick={() => {
                            onOutletToolChange(
                                outletTool === type ? null : type
                            )
                            onNoWritingSurfaceToolChange(false)
                            onSeatStructureToolChange(null)
                        }}>
                        <i className={`bi ${icon} me-2`}/>{label}
                    </Button>)}
            </div>}
        <Card body>
            <div className="border rounded bg-body"
                style={{overflowX: "auto", overflowY: "hidden"}}>
                <div className="position-relative"
                    onClick={event => {
                        if (!editing || (
                            !outletTool && seatStructureTool !== "ADD"
                        )) return
                        const rect = event.currentTarget.getBoundingClientRect()
                        const position = {
                            x: Math.max(0, Math.min(1,
                                (event.clientX - rect.left) / rect.width)),
                            y: Math.max(0, Math.min(1,
                                (event.clientY - rect.top) / rect.height)),
                        }
                        if (seatStructureTool === "ADD") {
                            onCreateSeat(position)
                        } else if (outletTool) {
                            onCreateOutlet(outletTool, position)
                        }
                    }}
                    style={{height: 720, minWidth: 1400}}>
                    <div className="position-absolute rounded border
                        border-secondary-subtle text-center"
                        style={{left: "3%", right: "3%", top: 4, height: 28,
                            lineHeight: "26px", background: "rgba(108,117,125,.08)",
                            letterSpacing: ".08em", fontSize: ".78rem",
                            fontWeight: 600}}>
                        ↑ VORNE · PROJEKTIONSFLÄCHE / TAFEL ↑
                    </div>
                    {frames.map(frame => <div key={frame.key}
                        className="position-absolute rounded border"
                        style={{left: frame.left, width: frame.width,
                            top: frame.top, height: frame.height,
                            background: frame.index % 2
                                ? "rgba(25,135,84,.04)"
                                : "rgba(253,126,20,.045)"}}>
                        <div className="position-absolute start-50
                            translate-middle-x bg-body border rounded-pill
                            px-3 py-1 fw-semibold"
                            style={{top: -8, whiteSpace: "nowrap", zIndex: 2}}>
                            {frame.name}
                        </div>
                    </div>)}
                    {data.installation?.outlets.filter(outlet =>
                        !outlet.seatKey && outlet.position).map(outlet => {
                        const circuit = outlet.circuitKey
                            ? circuitsByKey.get(outlet.circuitKey) : undefined
                        const symbol = outlet.type === "FLOOR" ? "B"
                            : outlet.type === "LECTERN" ? "P" : "⌁"
                        return <button key={outlet.key} type="button"
                            title={outlet.label || outlet.type}
                            onClick={event => {
                                event.stopPropagation()
                                if (editing) onEditOutlet(outlet, circuit)
                            }}
                            style={{position: "absolute",
                                left: `${outlet.position!.x * 100}%`,
                                top: `${outlet.position!.y * 100}%`,
                                width: 24, height: 24, padding: 0,
                                borderRadius: outlet.type === "FLOOR" ? 4 : "50%",
                                transform: "translate(-50%, -50%)",
                                border: `3px solid ${outlet.needsCircuitMapping
                                    ? "#ffc107" : "#dc3545"}`,
                                background: "#fff", color: "#212529",
                                fontSize: ".7rem", fontWeight: 700, zIndex: 4}}>
                            {symbol}
                        </button>
                    })}
                    {data.seatPlan?.seats.map(seat => {
                        const outlet = outletsBySeat.get(seat.key)
                        const exam = examSeats.has(seat.key)
                        const noSurface = !seat.hasWritingSurface
                        const unusedRow = !examRows.has(
                            `${seat.section}:${seat.row}`)
                        const validation = validations.get(seat.key)
                        const validationColor = validation?.state === "VERIFIED"
                            ? "#198754" : validation?.state === "DEVIATION"
                                ? "#dc3545" : validation?.state === "PENDING"
                                    ? "#ffc107" : undefined
                        const hovered = hoveredSeatKey === seat.key
                        return <span key={seat.key} aria-label={seat.key}
                            onClick={event => {
                                event.stopPropagation()
                                if (!editing) return
                                if (noWritingSurfaceTool) {
                                    onToggleWritingSurface(seat)
                                    return
                                }
                                if (seatStructureTool === "DELETE") {
                                    onDeleteSeat(seat)
                                    return
                                }
                                if (seatStructureTool === "ADD") return
                                const circuit = outlet?.circuitKey
                                    ? circuitsByKey.get(outlet.circuitKey)
                                    : undefined
                                onEditSeat(seat, outlet, circuit)
                            }}
                            onMouseEnter={() => setHoveredSeatKey(seat.key)}
                            onMouseLeave={() => setHoveredSeatKey(null)}
                            style={{position: "absolute",
                                left: `${seat.position.x * 100}%`,
                                top: `${seat.position.y * 100}%`,
                                width: 14, height: 11, borderRadius: 3,
                                transform: "translate(-50%, -50%)",
                                background: noSurface
                                    ? "repeating-linear-gradient(135deg,#ffc107 0 3px,#212529 3px 5px)"
                                    : exam ? "#0d6efd"
                                        : unusedRow ? "#495057" : "#ced4da",
                                border: noSurface ? "2px solid #212529"
                                    : outlet ? "3px solid #dc3545"
                                        : unusedRow ? "2px solid #212529"
                                            : "1px solid #6c757d",
                                boxShadow: hovered
                                    ? "0 0 0 4px rgba(255,193,7,.55),0 6px 14px rgba(0,0,0,.35)"
                                    : validationColor
                                        ? `0 0 0 2px ${validationColor}` : undefined,
                                scale: hovered ? "1.65" : "1",
                                transition: "scale .12s ease, box-shadow .12s ease",
                                cursor: editing
                                    ? noWritingSurfaceTool
                                        ? "crosshair" : "pointer"
                                    : "help",
                                zIndex: hovered ? 5 : exam || outlet ? 1 : 0}}/>
                    })}
                    {hoveredSeat && <SeatPopover
                        seat={hoveredSeat}
                        outlet={outletsBySeat.get(hoveredSeat.key)}
                        circuit={(() => {
                            const key = outletsBySeat.get(hoveredSeat.key)
                                ?.circuitKey
                            return key ? circuitsByKey.get(key) : undefined
                        })()}
                        examNumber={examSeatNumbers.get(hoveredSeat.key)}
                        neighborCount={neighborCounts.get(hoveredSeat.key) ?? 0}
                        validation={validations.get(hoveredSeat.key)}
                        unusedRow={!examRows.has(
                            `${hoveredSeat.section}:${hoveredSeat.row}`)}
                    />}
                </div>
            </div>
            <SeatLegend hasSurvey={Boolean(data.survey)}/>
        </Card>
    </>
}

const SeatPopover = ({
    seat,
    outlet,
    circuit,
    examNumber,
    neighborCount,
    validation,
    unusedRow,
}: {
    seat: Seat
    outlet?: Outlet
    circuit?: Circuit
    examNumber?: number
    neighborCount: number
    validation?: Survey["seatValidations"][number]
    unusedRow: boolean
}) =>
    <div className="position-absolute card shadow"
        style={{left: `${seat.position.x * 100}%`,
            top: `${seat.position.y * 100}%`, width: 245,
            transform: seat.position.x > .72
                ? "translate(calc(-100% - 16px), -50%)"
                : "translate(16px, -50%)",
            zIndex: 10, pointerEvents: "none"}}>
        <div className="card-header py-2 d-flex justify-content-between">
            <strong>{seat.key}</strong>
            <Badge bg={examNumber ? "primary" : "secondary"}>
                {examNumber ? `Aufteilung: Platz ${examNumber}` : "Nicht belegt"}
            </Badge>
        </div>
        <div className="card-body py-2 small">
            <div><strong>Bereich:</strong> {seat.section}, Reihe {seat.row},
                Platz {seat.column}</div>
            <div className={outlet ? "text-danger fw-semibold" : "text-muted"}>
                {outlet ? `${outlet.socketCount}× Schuko · ` +
                    `${circuit?.label ?? "Sicherung unbekannt"}`
                    : "Keine feste Steckdose am Sitz"}
            </div>
            <div className="text-muted">{neighborCount} erreichbare Nachbarsitze</div>
            {validation && <div><strong>Vermessung:</strong> {
                validation.state === "VERIFIED" ? "verifiziert"
                    : validation.state === "DEVIATION" ? "Abweichung" : "offen"
            }</div>}
            {validation?.metricPosition && <div>
                <strong>Reale Position:</strong>{" "}
                x {validation.metricPosition.x.toFixed(2)} m,{" "}
                y {validation.metricPosition.y.toFixed(2)} m
            </div>}
            {!seat.hasWritingSurface && <div className="text-danger fw-semibold">
                Keine Schreibfläche · nicht als Prüfungsplatz zulässig
            </div>}
            {seat.hasWritingSurface && unusedRow &&
                <div className="fw-semibold" style={{color: "#6f42c1"}}>
                    In dieser Aufteilung keine Prüfungsreihe
                </div>}
        </div>
    </div>

const SeatLegend = ({hasSurvey}: {hasSurvey: boolean}) =>
    <div className="d-flex flex-wrap gap-3 mt-2 small">
        <span><Badge bg="secondary">▰</Badge> physischer Sitz</span>
        <span><Badge bg="primary">▰</Badge> in Aufteilung belegt</span>
        <span className="text-warning">▰ keine Schreibfläche</span>
        <span style={{color: "#495057"}}>▰ Reihe ohne Prüfungsplätze</span>
        <span className="text-danger">▰ feste Steckdose</span>
        <span>B/P freie Boden-/Pult-/Wandsteckdose</span>
        {hasSurvey && <>
            <span style={{color: "#ffc107"}}>● Messung offen</span>
            <span style={{color: "#198754"}}>● verifiziert</span>
            <span style={{color: "#dc3545"}}>● Abweichung</span>
        </>}
    </div>
